import { Component, Input, OnDestroy, SimpleChanges } from '@angular/core'
import * as Highcharts from 'highcharts'

@Component({
    selector: 'app-lsr-sparkline-chart',
    templateUrl: './lsr-sparkline-chart.component.html',
    standalone: false
})
export class LsrSparklineChartComponent implements OnDestroy {
  @Input() chartData: any

  public highChart: typeof Highcharts = Highcharts
  public highChartOptions: Highcharts.Options

  private _chartData: {
    lsr: number[]
    time: string[]
  }

  public getHighChartOptions(): Highcharts.Options {
    const lsr: number[] = this._chartData.lsr
    const time: string[] = this._chartData.time
    const self: this = this
    const maxTT: number = Math.abs(Math.max(...lsr))

    return {
      chart: {
        backgroundColor: 'transparent',
        borderWidth: 0,
        width: 200,
        height: 120,
      },
      exporting: {
        enabled: false,
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        hideDelay: 0,
        outside: true,
        formatter: function(): string {
          const _lsr: number = lsr[this.point.index] ? lsr[this.point.index] : null
          const _time: string = time[this.point.index] ? time[this.point.index] : null
          return `<div>
            <span>Logic Solver Response Time: <b>${_lsr > 0 ? _lsr + 's' : 'No Response'}</b></span>
            <br>
            <span>Time:</span>
            <br>
            <span><b>${self.formatDate(_time)}</b></span>
          </div>`
        },
        style: {
          fontSize: '12px',
          fontFamily: 'Arial, sans-serif',
          zIndex: 99999,
        },
      },
      plotOptions: {
        series: {
          animation: false,
          shadow: false,
          lineWidth: 1,
        },
        column: {
          stacking: 'normal',
          opacity: 0.9,
          pointWidth: 14,
        },
      },
      yAxis: {
        max: maxTT,
        lineWidth: 1,
        labels: {
          enabled: true,
          formatter: function(): string {
            return `${this.value}`
          },
          style: {
            fontSize: '10px',
            fontFamily: 'Arial, sans-serif',
          },
        },
        title: {
          text: null,
        },
      },
      xAxis: {
        visible: false,
        labels: {
          enabled: false,
        },
      },
      series: [
        {
          type: 'column',
          data: this.getDataSeries(lsr, maxTT),
        },
      ],
    }
  }

  private getDataSeries(lsr: number[], maxTT: number): any[] {
    if (this.hasOnlyNoResponse(lsr)) {
      return [
        ...lsr.map((v: number, i: number) => {
          return {
            y: 0.25,
            color: '#bfbfbf',
            borderColor: '#bfbfbf',
            borderWidth: 1.5,
          }
        }),
      ]
    }
    return [
      ...lsr.map((v: number, i: number) => {
        return {
          y: v > 0 ? v : Math.max(maxTT, 0.25),
          color: v > 0 ? '#50a0e1' : '#bfbfbf',
          borderColor: v > 0 ? '#50a0e1' : '#bfbfbf',
          borderWidth: 1.5,
        }
      }),
    ]
  }

  private hasOnlyNoResponse(lsr: number[]): boolean {
    const onlyNoResponse: number[] = lsr.filter(t => t < 0)
    return onlyNoResponse.length === lsr.length
  }

  private formatDate(date: string): string {
    const elemFullDate: string[] = date
      .replace('T', ' ')
      .replace('Z', '')
      .split(' ')
    const elemDate: string[] = elemFullDate[0].split('-')
    if (elemFullDate.length < 2) {
      return `${elemDate[2]}-${elemDate[1]}-${elemDate[0]}`
    }
    return `${elemDate[2]}-${elemDate[1]}-${elemDate[0]} ${elemFullDate[1]}`
  }

  private _drawHighChart(): void {
    if (this.hasData()) {
      this.highChartOptions = this.getHighChartOptions()
    } else {
      this.highChartOptions = undefined
    }
  }

  public hasData(): boolean {
    return this._chartData && this._chartData.lsr.length > 0
  }

  ngOnChanges(change: SimpleChanges): void {
    if (JSON.stringify(this._chartData) != JSON.stringify(change.chartData.currentValue)) {
      setTimeout((): void => {
        this._chartData = change.chartData.currentValue
        this._drawHighChart()
      }, 100)
    }
  }

  ngOnDestroy(): void {
    this._chartData = undefined
    this.highChartOptions = undefined
  }
}
